/**
 * Global Price WebSocket Manager
 *
 * Maintains singleton WebSocket connections for watchlist price feeds.
 * Internally multiplexes many symbols over one account-level socket.
 *
 * Usage:
 *   const unsub = globalPriceManager.subscribe(symbol, accountId, timeframe, wsOrigin, callback);
 *   // callback receives { bid, ask } objects
 *   // call unsub() to unsubscribe (connection stays if other subscribers exist)
 */

const streams = new Map();
const HEARTBEAT_INTERVAL_MS = 60_000;

function normalizeSymbol(symbol) {
  return String(symbol || '').trim().toUpperCase();
}

function compactSymbol(symbol) {
  return normalizeSymbol(symbol).replace(/[^A-Z0-9]/g, '');
}

function getSymbolKeys(symbol) {
  const normal = normalizeSymbol(symbol);
  const compact = compactSymbol(symbol);
  return [...new Set([normal, compact].filter(Boolean))];
}

function makeStreamKey(accountId, wsOrigin) {
  return `${accountId}::${wsOrigin}`;
}

function getActiveSymbols(entry) {
  const symbols = [];
  const seenEntries = new Set();
  const seenSymbols = new Set();
  entry.symbols.forEach((symbolEntry) => {
    if (seenEntries.has(symbolEntry)) return;
    seenEntries.add(symbolEntry);
    if (symbolEntry.subscribers.size > 0 && !seenSymbols.has(symbolEntry.requestedSymbol)) {
      seenSymbols.add(symbolEntry.requestedSymbol);
      symbols.push(symbolEntry.requestedSymbol);
    }
  });
  return symbols;
}

function getKnownSymbols(entry) {
  const symbols = [];
  const seenEntries = new Set();
  const seenSymbols = new Set();
  entry.symbols.forEach((symbolEntry) => {
    if (seenEntries.has(symbolEntry)) return;
    seenEntries.add(symbolEntry);
    if (!seenSymbols.has(symbolEntry.requestedSymbol)) {
      seenSymbols.add(symbolEntry.requestedSymbol);
      symbols.push(symbolEntry.requestedSymbol);
    }
  });
  return symbols;
}

function hasSubscribers(entry) {
  for (const symbolEntry of entry.symbols.values()) {
    if (symbolEntry.subscribers.size > 0) return true;
  }
  return false;
}

function hasKnownSymbols(entry) {
  return getKnownSymbols(entry).length > 0;
}

function sendJson(ws, payload) {
  try {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
  } catch (_) {}
  return false;
}

function sendSubscribe(entry, symbols) {
  if (!symbols.length) return;
  sendJson(entry.ws, {
    action: 'subscribe_many',
    symbols,
    timeframe: entry.timeframe || 'M15',
  });
}

function sendDetach(entry, symbols) {
  if (!symbols.length) return;
  sendJson(entry.ws, {
    action: 'detach_many',
    symbols,
  });
}

function connect(entry) {
  if (!entry || !entry.accountId || !entry.wsOrigin) return;
  if (entry.ws && entry.ws.readyState < WebSocket.CLOSING) return;

  if (entry.reconnectTimer) {
    clearTimeout(entry.reconnectTimer);
    entry.reconnectTimer = null;
  }
  const authToken = localStorage.getItem('token');
  if (!authToken) return;

  try {
    const ws = new WebSocket(`${entry.wsOrigin}/ws/prices/${entry.accountId}/?token=${authToken}`);

    ws.onopen = () => {
      if (entry.ws !== ws) return;
      entry.reconnectDelay = 2000;
      sendSubscribe(entry, PERSIST_IDLE_STREAMS ? getKnownSymbols(entry) : getActiveSymbols(entry));
      if (entry.heartbeatTimer) clearInterval(entry.heartbeatTimer);
      entry.heartbeatTimer = setInterval(() => {
        sendJson(ws, { action: 'heartbeat', ts: Date.now() });
      }, HEARTBEAT_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(String(event.data).replace(/NaN/g, 'null'));
        if (message?.type === 'live_price' || message?.data?.bid != null || message?.data?.ask != null) {
          const bid = message?.data?.bid ?? message?.bid ?? null;
          const ask = message?.data?.ask ?? message?.ask ?? null;
          if (bid == null && ask == null) return;
          const msgSymbol = message?.symbol ?? message?.data?.symbol ?? message?.price?.symbol;
          const targetEntries = [];
          getSymbolKeys(msgSymbol).forEach((symbolKey) => {
            const symbolEntry = entry.symbols.get(symbolKey);
            if (symbolEntry && !targetEntries.includes(symbolEntry)) {
              targetEntries.push(symbolEntry);
            }
          });
          targetEntries.forEach((symbolEntry) => {
            symbolEntry.lastPrice = { bid, ask };
            symbolEntry.subscribers.forEach((cb) => {
              try { cb({ bid, ask }); } catch (_) {}
            });
          });
        }
      } catch (_) {}
    };

    ws.onerror = () => {};

    ws.onclose = () => {
      if (entry.ws !== ws) return;
      if (entry.heartbeatTimer) {
        clearInterval(entry.heartbeatTimer);
        entry.heartbeatTimer = null;
      }
      entry.ws = null;
      if (PERSIST_IDLE_STREAMS ? !hasKnownSymbols(entry) : !hasSubscribers(entry)) return;
      const delay = entry.reconnectDelay || 2000;
      entry.reconnectTimer = setTimeout(() => {
        connect(entry);
      }, delay);
      entry.reconnectDelay = Math.min(delay * 1.5, 30000);
    };

    entry.ws = ws;
  } catch (err) {
    console.warn('[GlobalPriceManager] WS open failed', entry.accountId, err);
  }
}

// Keep watchlist streams alive for the authenticated browser session. This
// makes route changes cheap: remounted widgets reattach to an existing socket
// and receive the latest cached price without a fresh broker subscription.
const PERSIST_IDLE_STREAMS = true;
const IDLE_CLOSE_DELAY = 90_000;

function subscribe(symbol, accountId, timeframe, wsOrigin, callback) {
  if (!symbol || !accountId || !wsOrigin || typeof callback !== 'function') {
    return () => {};
  }

  const streamKey = makeStreamKey(accountId, wsOrigin);
  let entry = streams.get(streamKey);

  if (!entry) {
    entry = {
      accountId,
      wsOrigin,
      timeframe: timeframe || 'M15',
      ws: null,
      symbols: new Map(),
      reconnectTimer: null,
      reconnectDelay: 2000,
      idleTimer: null,
      heartbeatTimer: null,
    };
    streams.set(streamKey, entry);
  } else {
    entry.timeframe = entry.timeframe || timeframe || 'M15';
    if (entry.idleTimer) { clearTimeout(entry.idleTimer); entry.idleTimer = null; }
  }

  const primaryKey = compactSymbol(symbol) || normalizeSymbol(symbol);
  let symbolEntry = entry.symbols.get(primaryKey);
  const wasNewSymbol = !symbolEntry;
  if (!symbolEntry) {
    symbolEntry = {
      requestedSymbol: symbol,
      subscribers: new Set(),
      idleTimer: null,
      keys: getSymbolKeys(symbol),
      lastPrice: null,
    };
    symbolEntry.keys.forEach((key) => entry.symbols.set(key, symbolEntry));
  } else if (symbolEntry.idleTimer) {
    clearTimeout(symbolEntry.idleTimer);
    symbolEntry.idleTimer = null;
  }

  symbolEntry.subscribers.add(callback);
  if (symbolEntry.lastPrice) {
    setTimeout(() => {
      if (symbolEntry.subscribers.has(callback)) {
        try { callback(symbolEntry.lastPrice); } catch (_) {}
      }
    }, 0);
  }

  if (!entry.ws || entry.ws.readyState >= WebSocket.CLOSING) {
    connect(entry);
  } else if (wasNewSymbol && entry.ws.readyState === WebSocket.OPEN) {
    sendSubscribe(entry, [symbolEntry.requestedSymbol]);
  }

  // Return unsubscribe function
  return () => {
    const e = streams.get(streamKey);
    if (!e) return;
    const currentSymbolEntry = e.symbols.get(primaryKey);
    if (!currentSymbolEntry) return;
    currentSymbolEntry.subscribers.delete(callback);

    if (PERSIST_IDLE_STREAMS) return;

    if (currentSymbolEntry.subscribers.size === 0 && !currentSymbolEntry.idleTimer) {
      currentSymbolEntry.idleTimer = setTimeout(() => {
        const cur = streams.get(streamKey);
        if (!cur) return;
        const latest = cur.symbols.get(primaryKey);
        if (!latest || latest.subscribers.size > 0) return;
        sendDetach(cur, [latest.requestedSymbol]);
        latest.keys.forEach((key) => {
          if (cur.symbols.get(key) === latest) cur.symbols.delete(key);
        });
        latest.idleTimer = null;
      }, IDLE_CLOSE_DELAY);
    }

    if (!hasSubscribers(e) && !e.idleTimer) {
      e.idleTimer = setTimeout(() => {
        const cur = streams.get(streamKey);
        if (!cur || hasSubscribers(cur)) return; // re-subscribed in the meantime
        if (cur.reconnectTimer) clearTimeout(cur.reconnectTimer);
        if (cur.ws) {
          sendJson(cur.ws, { action: 'unsubscribe' });
          try { cur.ws.close(); } catch (_) {}
        }
        cur.symbols.forEach((symbolEntryForCleanup) => {
          if (symbolEntryForCleanup.idleTimer) clearTimeout(symbolEntryForCleanup.idleTimer);
        });
        streams.delete(streamKey);
      }, IDLE_CLOSE_DELAY);
    }
  };
}

function closeAll() {
  streams.forEach((entry) => {
    if (entry.reconnectTimer) clearTimeout(entry.reconnectTimer);
    if (entry.idleTimer) clearTimeout(entry.idleTimer);
    if (entry.heartbeatTimer) clearInterval(entry.heartbeatTimer);
    entry.symbols.forEach((symbolEntry) => {
      if (symbolEntry.idleTimer) clearTimeout(symbolEntry.idleTimer);
    });
    if (entry.ws) {
      sendJson(entry.ws, { action: 'unsubscribe' });
      try { entry.ws.close(); } catch (_) {}
    }
  });
  streams.clear();
}

// Close all connections when user leaves the website
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', closeAll);
  window.addEventListener('valpha:logout', closeAll);
}

const globalPriceManager = { subscribe, closeAll };
export default globalPriceManager;
